import { useState, useEffect } from "react";
import { Link } from "@/lib/router-compat";
import { supabaseProjectManager, LeanProject } from "@/lib/supabaseProjectManager";
import { Camera, Video, Share2, ArrowRight } from "lucide-react";
import { LazyCoverImage } from "@/components/LazyCoverImage";

interface RelatedProjectsProps {
  currentProjectId: string;
  currentCategory: string;
}

const RelatedProjectCard = ({ project, Icon }: { project: LeanProject; Icon: any }) => {
  return (
    <Link 
      to={`/portfolio/${project.slug}`} 
      className="group block"
    >
      <div className="relative overflow-hidden rounded-xl bg-card border border-border transition-all duration-500 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/20 hover:-translate-y-2">
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <LazyCoverImage
            projectId={project.id}
            category={project.category as "photography" | "videography" | "social-media"}
            title={project.title}
            className="w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity" />
          
          {/* Category Badge */}
          <div className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-background/80 backdrop-blur-xs border border-border">
            <Icon className="w-4 h-4 text-primary" />
            <span className="text-xs capitalize">
              {project.category.replace("-", " ")}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-1">
            {project.title}
          </h3>
          <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
            {project.description}
          </p>
          
          <div className="flex items-center gap-2 text-primary text-sm font-medium">
            <span>Projekt ansehen</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </Link>
  );
};

const RelatedProjects = ({ currentProjectId, currentCategory }: RelatedProjectsProps) => {
  const [relatedProjects, setRelatedProjects] = useState<LeanProject[]>([]);

  useEffect(() => {
    const loadRelatedProjects = async () => {
      try {
        // Lean list (no image arrays): the covers load lazily per card
        const allProjects = await supabaseProjectManager.getProjectsLean({ publicOnly: true });
        
        // Filter out current project
        const otherProjects = allProjects.filter(p => p.id !== currentProjectId);
        
        // Get projects from same category
        const sameCategory = otherProjects.filter(p => p.category === currentCategory);
        
        // Get projects from other categories
        const otherCategories = otherProjects.filter(p => p.category !== currentCategory);
        
        // Shuffle for randomness
        const shuffled = [...sameCategory].sort(() => Math.random() - 0.5);
        const shuffledOther = [...otherCategories].sort(() => Math.random() - 0.5);
        
        // Take up to 3 projects, prioritizing same category
        const selected = [...shuffled.slice(0, 3)];
        if (selected.length < 3) {
          selected.push(...shuffledOther.slice(0, 3 - selected.length));
        }
        
        setRelatedProjects(selected);
      } catch (error) {
        console.error('Error loading related projects:', error);
      }
    };
    loadRelatedProjects();
  }, [currentProjectId, currentCategory]);

  const categoryIcons = {
    photography: Camera,
    videography: Video,
    "social-media": Share2,
  };

  if (relatedProjects.length === 0) {
    return null;
  }

  return (
    <section className="section-padding border-t border-border bg-gradient-to-b from-background to-secondary/10">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Mehr Projekte entdecken
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Entdecken Sie weitere beeindruckende Arbeiten aus unserem Portfolio
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {relatedProjects.map((project) => {
            const Icon = categoryIcons[project.category as keyof typeof categoryIcons];
            return (
              <RelatedProjectCard 
                key={project.id} 
                project={project} 
                Icon={Icon} 
              />
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RelatedProjects;
